import { Badge, Button, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { AdminAssessment } from '@/services/adminService';
import { AssessmentStatus } from '@/services/api';
import { SCORE_LABEL_COLORS } from '@/utils';
import { formatUserName } from '@/utils/formatters';
import type { ResultsSortOrder } from './useSortOrderState';
import styles from './AdminAssessments.module.scss';

export const buildAssessmentColumns = (
  onView: (id: string) => void,
  dateSortOrder: ResultsSortOrder,
): ColumnsType<AdminAssessment> => [
  {
    title: 'User',
    key: 'user',
    width: 180,
    ellipsis: true,
    render: (_, record) => {
      // A guest has only a synthetic name/email, so the access code's tags are
      // the meaningful identifier here instead.
      if (record.accessCodeTags !== null) {
        return record.accessCodeTags.length > 0 ? (
          <>
            {record.accessCodeTags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </>
        ) : (
          <Typography.Text type="secondary">Guest</Typography.Text>
        );
      }
      return (
        formatUserName(record.user.firstName, record.user.lastName) || (
          <Typography.Text type="secondary">{record.user.email}</Typography.Text>
        )
      );
    },
  },
  {
    title: 'Email',
    key: 'email',
    width: 220,
    ellipsis: true,
    render: (_, record) =>
      record.accessCodeTags !== null ? (
        <Typography.Text type="secondary">—</Typography.Text>
      ) : (
        record.user.email
      ),
  },
  {
    title: 'Package',
    key: 'interviewTemplate',
    width: 200,
    ellipsis: true,
    render: (_, record) => record.interviewTemplate.name,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    render: (status: AssessmentStatus) => (
      <Tag color={status === AssessmentStatus.Completed ? 'success' : 'default'}>
        {status === AssessmentStatus.Completed ? 'Completed' : 'In Progress'}
      </Tag>
    ),
  },
  {
    title: 'Score',
    key: 'score',
    width: 170,
    render: (_, record) => {
      if (record.totalScore === null) return <Typography.Text type="secondary">—</Typography.Text>;
      return (
        <span className={styles.scoreCell}>
          <Tag color={record.label ? SCORE_LABEL_COLORS[record.label] : 'default'}>{record.label}</Tag>
          <Typography.Text type="secondary">{record.totalScore.toFixed(1)}</Typography.Text>
        </span>
      );
    },
  },
  {
    title: 'Flagged',
    key: 'flagged',
    width: 90,
    render: (_, record) =>
      record.reviewFlagged ? (
        <Badge status="error" text="Yes" />
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
  },
  {
    title: 'Date',
    key: 'date',
    width: 130,
    sorter: (_a, _b) => 0,
    sortOrder: dateSortOrder,
    render: (_, record) =>
      new Date(record.completedAt ?? record.createdAt).toLocaleDateString(),
  },
  {
    title: '',
    key: 'actions',
    width: 135,
    fixed: 'right',
    render: (_, record) =>
      record.status === AssessmentStatus.Completed ? (
        <Button type="link" size="small" onClick={() => onView(record.id)}>
          View results
        </Button>
      ) : null,
  },
];
